/**
 * Vocabulary of the agnostic inbound webhook ledger (TO-323).
 *
 * The capability terminates HTTP deliveries from outside systems: it proves each
 * one authentic, keeps its bytes verbatim and announces it. It reads nothing out
 * of a body, so nothing here describes payload shape — these enums cover only
 * who sent a delivery and where it sits in its lifecycle.
 */

/**
 * The outside systems the platform accepts deliveries from.
 *
 * A data enum, deliberately: adding a provider is a member here, a signature
 * case in the verifier's switch and a registered secret — never a change to a
 * receiving, storage or announcement rule.
 *
 * `TEST` is the seed member. It exists so the capability can ship and be
 * exercised end to end before any external partner is committed, and it uses the
 * platform's own HMAC scheme rather than imitating a third party's.
 */
export enum WebhookProvider {
	TEST = 'test',
	/**
	 * Twilio (TO-474): message status callbacks on a tenant endpoint, and the
	 * shared Messaging Service's inbound SMS on the platform route. Verified with
	 * Twilio's own `X-Twilio-Signature` scheme, not the platform `v1` scheme.
	 */
	TWILIO = 'twilio',
}

/**
 * Where a recorded delivery sits in its lifecycle.
 *
 * Only {@link WebhookDeliveryStatus.RECEIVED} is reachable today: TO-323 ships
 * the receiving half of the ledger, and no consumer of `webhook.received`
 * exists yet. The remaining three members are the contract the first consumer
 * will claim rows into (`RECEIVED` → `PROCESSING` via an atomic compare-and-set,
 * then `PROCESSED` or `FAILED`). They ship now — with the `attempts`,
 * `error_message` and `processed_at` columns that back them — so that ticket
 * adds a consumer rather than a migration.
 */
export enum WebhookDeliveryStatus {
	RECEIVED = 'RECEIVED',
	PROCESSING = 'PROCESSING',
	PROCESSED = 'PROCESSED',
	FAILED = 'FAILED',
}

/** Upper bound on an accepted delivery body, in bytes (REQ-010). */
export const WEBHOOK_MAX_PAYLOAD_BYTES = 262_144

/**
 * How far from server time a delivery's signed timestamp may sit, in seconds
 * (REQ-008). The timestamp is inside the signed bytes, so it cannot be edited
 * without breaking the digest — this window is therefore what bounds how long a
 * captured request stays replayable.
 */
export const WEBHOOK_TIMESTAMP_TOLERANCE_SECONDS = 300

/** Header carrying the signed timestamp: an integer count of seconds since the Unix epoch. */
export const WEBHOOK_TIMESTAMP_HEADER = 'x-webhook-timestamp'

/** Header carrying the signature, of the form `v1=<64 lowercase hex characters>`. */
export const WEBHOOK_SIGNATURE_HEADER = 'x-webhook-signature'

/** Header carrying Twilio's base64 HMAC-SHA1 signature of a `twilio` delivery (TO-474). */
export const TWILIO_SIGNATURE_HEADER = 'x-twilio-signature'

/** The only body media type a `twilio` delivery is verified under (TO-474 REQ-008). */
export const TWILIO_FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded'

/**
 * The signature scheme version the headers are produced under.
 *
 * It prefixes both the header value (`v1=<digest>`) and the signing input
 * (`v1:{timestamp}:{body}`), so a future scheme becomes an additional accepted
 * prefix rather than a breaking change for every registered partner.
 */
export const WEBHOOK_SIGNATURE_VERSION = 'v1'
